import { formatLocaleMessage } from '@/utils/formatLocaleMessage';

export interface ApiErrorPayload {
  code?: string;
  error?: string;
  message?: string | string[];
  reason?: string;
  details?: Record<string, unknown>;
  fields?: string[];
  value_options?: string[];
}

const PURCHASE_ORDER_RECEIVING_FIELD_LABELS: Record<string, string> = {
  items: 'purchaseOrder.receivingModal.errorField.items',
  product_id: 'purchaseOrder.receivingModal.errorField.productId',
  purchase_order_item_id: 'purchaseOrder.receivingModal.errorField.purchaseOrderItemId',
  quantity_received: 'purchaseOrder.receivingModal.errorField.quantityReceived',
  quantity_ordered: 'purchaseOrder.receivingModal.errorField.quantityOrdered',
  expected_previous_quantity_received:
    'purchaseOrder.receivingModal.errorField.expectedPreviousQuantityReceived',
  accepted_quantity: 'purchaseOrder.receivingModal.errorField.acceptedQuantity',
  quantity_received_total: 'purchaseOrder.receivingModal.errorField.totalQuantityReceived',
  barcode: 'purchaseOrder.receivingModal.errorField.barcode',
  warehouse_location: 'purchaseOrder.receivingModal.errorField.warehouseLocation',
  carton_spec_quantity: 'purchaseOrder.receivingModal.errorField.cartonSpecQuantity',
  warehouse_packaging_type: 'purchaseOrder.receivingModal.errorField.warehousePackagingType',
  expiry_date: 'purchaseOrder.receivingModal.errorField.expiryDate',
  expiry_date_type: 'purchaseOrder.receivingModal.errorField.expiryDateType',
  after_opening_months: 'purchaseOrder.receivingModal.errorField.afterOpeningMonths',
  received_date: 'purchaseOrder.receivingModal.errorField.receivedDate',
};

const PAYMENT_REQUEST_FIELD_LABELS: Record<string, string> = {
  invoice_file_id: 'requestError.paymentRequest.field.invoiceFile',
  payment_region_code: 'purchaseOrder.payment.requestModal.paymentRegion.label',
  bank_country_code: 'requestError.paymentRequest.field.bankCountryCode',
  bank_code: 'requestError.paymentRequest.field.bankCode',
  branch_code: 'requestError.paymentRequest.field.branchCode',
  local_clearing_system: 'requestError.paymentRequest.field.localClearingSystem',
  swift_code: 'purchaseOrder.fromOffer.swiftCode',
  bsb_code: 'purchaseOrder.paymentInfo.bsb.label',
  iban_code: 'purchaseOrder.paymentInfo.iban.label',
  account_routing_type1: 'requestError.paymentRequest.field.accountRoutingType',
  account_routing_value1: 'requestError.paymentRequest.field.accountRoutingValue',
  bank_account_no: 'purchaseOrder.fromOffer.accountNumber',
  bank_account_name: 'purchaseOrder.fromOffer.accountName',
};

const WINDOWS_1252_BYTE_OVERRIDES: Record<string, number> = {
  '€': 0x80,
  '‚': 0x82,
  ƒ: 0x83,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  ˆ: 0x88,
  '‰': 0x89,
  Š: 0x8a,
  '‹': 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '˜': 0x98,
  '™': 0x99,
  š: 0x9a,
  '›': 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

const MOJIBAKE_MARKER_PATTERN = /(?:[ÃÂ]|ã€|ï¼|[æèçå]|�)/;

const scoreReadableText = (text: string): number => {
  const cjkCount = text.match(/[\u3400-\u9fff]/g)?.length || 0;
  const markerCount = text.match(new RegExp(MOJIBAKE_MARKER_PATTERN.source, 'g'))?.length || 0;
  return cjkCount * 3 - markerCount * 2;
};

const decodeMojibakeOnce = (text: string): string => {
  const bytes: number[] = [];
  for (const char of Array.from(text)) {
    const override = WINDOWS_1252_BYTE_OVERRIDES[char];
    if (override !== undefined) {
      bytes.push(override);
      continue;
    }

    const code = char.charCodeAt(0);
    if (code > 0xff) {
      return text;
    }
    bytes.push(code);
  }

  try {
    return decodeURIComponent(
      bytes.map((byte) => `%${byte.toString(16).padStart(2, '0')}`).join(''),
    );
  } catch {
    return text;
  }
};

export const repairMojibakeText = (value?: string): string => {
  const source = String(value ?? '');
  if (!MOJIBAKE_MARKER_PATTERN.test(source)) {
    return source;
  }

  let current = source;
  let best = source;
  let bestScore = scoreReadableText(source);

  for (let index = 0; index < 3; index += 1) {
    const decoded = decodeMojibakeOnce(current);
    if (!decoded || decoded === current) {
      break;
    }

    const score = scoreReadableText(decoded);
    if (score > bestScore) {
      best = decoded;
      bestScore = score;
    }
    current = decoded;
  }

  return best;
};

const humanizeFieldName = (field: string): string =>
  field.split('_').filter(Boolean).join(' ').trim() || field;

const formatPurchaseOrderReceivingField = (field: string): string => {
  const localeKey = PURCHASE_ORDER_RECEIVING_FIELD_LABELS[field];
  if (!localeKey) {
    return humanizeFieldName(field);
  }

  return formatLocaleMessage(localeKey, undefined, humanizeFieldName(field));
};

const formatPaymentRequestField = (field: string): string => {
  const localeKey = PAYMENT_REQUEST_FIELD_LABELS[field];
  if (!localeKey) {
    return humanizeFieldName(field);
  }

  return formatLocaleMessage(localeKey, undefined, humanizeFieldName(field));
};

const formatFieldList = (fields: string[], formatter: (field: string) => string): string =>
  fields.map((field) => formatter(field)).join('、');

const formatPurchaseOrderStatus = (status: string): string => {
  const localeKey = `purchaseOrder.status.${status}`;
  return formatLocaleMessage(localeKey, undefined, status);
};

const normalizePayload = (input: any): ApiErrorPayload | undefined => {
  if (!input) {
    return undefined;
  }

  if (input?.response?.data && typeof input.response.data === 'object') {
    return input.response.data as ApiErrorPayload;
  }

  if (typeof input === 'object') {
    return input as ApiErrorPayload;
  }

  return undefined;
};

const getRawMessage = (payload?: ApiErrorPayload): string | undefined => {
  if (!payload) {
    return undefined;
  }

  if (Array.isArray(payload.message)) {
    return repairMojibakeText(payload.message.join('; '));
  }

  if (typeof payload.message === 'string' && payload.message) {
    return repairMojibakeText(payload.message);
  }

  if (typeof payload.reason === 'string' && payload.reason) {
    return repairMojibakeText(payload.reason);
  }

  return undefined;
};

const getLocalizedRawApiMessage = (message?: string): string | undefined => {
  if (!message) {
    return undefined;
  }

  const trimmed = repairMojibakeText(message).trim();
  if (!trimmed) {
    return undefined;
  }

  const purchaseLogisticsMessages: Record<string, string> = {
    'Purchase type is required': 'purchaseOrder.purchaseType.required',
    'Purchase type must be regular or frozen': 'purchaseOrder.purchaseType.required',
    'Total pallets are required for new purchase orders': 'purchaseOrder.totalPalletCount.required',
    'Total pallets must be a non-negative number with at most one decimal place':
      'purchaseOrder.totalPalletCount.required',
    'Total pallets are required for each purchase order (non-negative, at most one decimal place)':
      'purchaseOrder.totalPalletCount.required',
    'Total pallets are required for each split purchase order': 'purchaseOrder.totalPalletCount.required',
    'Split purchase order pallet totals must equal the original total':
      'purchaseOrder.totalPalletCount.splitMismatch',
  };
  if (Object.prototype.hasOwnProperty.call(purchaseLogisticsMessages, trimmed)) {
    return formatLocaleMessage(purchaseLogisticsMessages[trimmed], undefined, trimmed);
  }

  let match = trimmed.match(/^Barcode "([^"]+)" is duplicated in this request$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.duplicateBarcodeInRequest',
      { barcode: match[1] },
      trimmed,
    );
  }

  if (/^Payment request already pending\.?$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequestPending', undefined, trimmed);
  }
  if (/^Purchase order not found$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.poNotFound', undefined, trimmed);
  }
  if (/^You do not have permission to request payment for this purchase order$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.noPermissionRequestPayment',
      undefined,
      trimmed,
    );
  }
  if (/^Invoice file not found for this purchase order$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.invoiceNotFound', undefined, trimmed);
  }
  if (/^Purchase order supplier missing$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.supplierMissing', undefined, trimmed);
  }
  if (/^請輸入IBAN賬號$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.ibanRequired', undefined, trimmed);
  }
  if (/^請輸入IBAN代碼$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.ibanCodeRequired', undefined, trimmed);
  }
  if (/^IBAN代碼格式錯誤，需為GB開頭且長度22$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.ibanGbInvalid', undefined, trimmed);
  }
  if (/^金額超過 100萬英鎊已自動改用 SWIFT，請補齊 SWIFT code\(BIC\)$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.autoSwiftNeedBic', undefined, trimmed);
  }
  if (/^CN 海外收款必须提供 swift_code 才能发起 SWIFT 转账$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.swiftRequiredCn', undefined, trimmed);
  }
  if (/^中國大陸付款地區僅支援 CNY 幣種$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.cnRegionCnyOnly', undefined, trimmed);
  }
  if (/^BSB代碼必填且为6位数字$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.bsbRequiredAu', undefined, trimmed);
  }
  if (/^Invalid document amount$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.documentAmountInvalid',
      undefined,
      trimmed,
    );
  }
  if (/^必須提供至少一種有效的 routing \/ bank identifier$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.routingIdentifierRequired',
      undefined,
      trimmed,
    );
  }
  if (/^付款資料不完整$/i.test(trimmed)) {
    return formatLocaleMessage('requestError.paymentRequest.dataIncomplete', undefined, trimmed);
  }
  if (
    /^payment_region_code 與 bank_country_code 必須一致$/i.test(trimmed) ||
    /^付款地區與銀行國家不一致$/i.test(trimmed)
  ) {
    return formatLocaleMessage(
      'requestError.paymentRequest.regionBankCountryMismatch',
      undefined,
      trimmed,
    );
  }
  if (/^付款路由資料不完整$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.routingDataIncomplete',
      undefined,
      trimmed,
    );
  }
  if (/^account_routing_type1 不符合當前付款路由$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.accountRoutingTypeInvalid',
      undefined,
      trimmed,
    );
  }
  if (/^韓國本地轉帳需提供 bank_code，且必須為 3 位數字。?$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.koreaBankCodeRequired',
      undefined,
      trimmed,
    );
  }
  if (/^韓國本地轉帳 bank_code 格式錯誤，必須為 3 位數字。?$/i.test(trimmed)) {
    return formatLocaleMessage(
      'requestError.paymentRequest.koreaBankCodeInvalid',
      undefined,
      trimmed,
    );
  }
  match = trimmed.match(
    /^香港本地銀行帳號格式錯誤，應為 8-17 位數(?:（分行代碼 \+ 帳號 ?）)?，目前為 (\d+) 位。?(?:格式示例：.+)?$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'requestError.paymentRequest.hkLocalAccountNumberInvalid',
      { length: Number(match[1]) },
      trimmed,
    );
  }

  if (/^Duplicate purchase order items detected in receive request$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.duplicateItemsInRequest',
      undefined,
      trimmed,
    );
  }

  if (/^Invalid quantity_received in receive request$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.invalidQuantityInRequest',
      undefined,
      trimmed,
    );
  }

  if (/^Suspiciously large quantities detected in receive request$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.suspiciousQuantityInRequest',
      undefined,
      trimmed,
    );
  }

  if (/^Invalid text field in receive request$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.invalidTextField',
      undefined,
      trimmed,
    );
  }

  if (/^Purchase Order Not Found$/i.test(trimmed) || /^Purchase order not found$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.orderNotFound',
      undefined,
      trimmed,
    );
  }

  if (/^Purchase order has no receiving location$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.missingReceivingLocation',
      undefined,
      trimmed,
    );
  }

  if (/^Please provide at least one item to receive\.?$/i.test(trimmed)) {
    return formatLocaleMessage('purchaseOrder.receivingModal.error.emptyItems', undefined, trimmed);
  }

  match = trimmed.match(/^Purchase order status "([^"]+)" cannot receive inventory\.?$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.invalidStatus',
      { status: formatPurchaseOrderStatus(match[1]) },
      trimmed,
    );
  }

  if (/^Location not found$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.locationNotFound',
      undefined,
      trimmed,
    );
  }

  if (/^Arrival location does not match the purchase order receiving location\.?$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.locationMismatch',
      undefined,
      trimmed,
    );
  }

  match = trimmed.match(/^Purchase order item ([^\s]+) not found$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.itemNotFound',
      { itemId: match[1] },
      trimmed,
    );
  }

  match = trimmed.match(/^Duplicate purchase order item ([^\s]+) detected$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.duplicateItem',
      { itemId: match[1] },
      trimmed,
    );
  }

  match = trimmed.match(
    /^Purchase order item ([^\s]+) does not belong to(?: purchase order [^\s]+| this purchase order)$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.itemNotInOrder',
      { itemId: match[1] },
      trimmed,
    );
  }

  match = trimmed.match(/^Product ([^\s]+) does not match purchase order item ([^\s]+)$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.productMismatch',
      { productId: match[1], itemId: match[2] },
      trimmed,
    );
  }

  match = trimmed.match(
    /^(accepted_quantity|expected_previous_quantity_received|quantity_received|quantity_received_total) must be an integer$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeInteger',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(
    /^(accepted_quantity|expected_previous_quantity_received|quantity_received|quantity_received_total) is out of INT32 range$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldOutOfRange',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(
    /^(accepted_quantity|expected_previous_quantity_received) cannot be negative$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldCannotBeNegative',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(/^Invalid received date: (.+)$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.invalidReceivedDate',
      { date: match[1] },
      trimmed,
    );
  }

  if (/^Another update is in progress for .+\. Please try again\.$/i.test(trimmed)) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.concurrentUpdate',
      undefined,
      trimmed,
    );
  }

  if (
    /^This purchase order was updated by another request\. Please refresh and retry\.$/i.test(
      trimmed,
    )
  ) {
    return formatLocaleMessage('purchaseOrder.receivingModal.error.staleData', undefined, trimmed);
  }

  if (
    /^Cumulative online presale quantity exceeds ordered quantity after this receipt\.?$/i.test(
      trimmed,
    ) ||
    /^online_presale_quantity cannot exceed remaining unreceived quantity$/i.test(trimmed)
  ) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.presaleExceedsOrderedAfterReceipt',
      undefined,
      trimmed,
    );
  }

  match = trimmed.match(
    /^The received quantity for product ([^\s]+) has changed\. Please refresh the purchase order and try again\.$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.staleProductQuantity',
      { productId: match[1] },
      trimmed,
    );
  }

  match = trimmed.match(/^Product with ID ([^\s]+) not found$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.productNotFound',
      { productId: match[1] },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must be an integer number$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeInteger',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must not be less than ([^ ]+)$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMin',
      { field: formatPurchaseOrderReceivingField(match[1]), min: match[2] },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must be a valid ISO 8601 date string$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldInvalidDate',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must be a string$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeString',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(
    /^([a-z_]+) must be a number(?: conforming to the specified constraints)?$/i,
  );
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeNumber',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must be one of the following values: (.+)$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeOneOf',
      {
        field: formatPurchaseOrderReceivingField(match[1]),
        values: match[2],
      },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) should not be empty$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldRequired',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  match = trimmed.match(/^([a-z_]+) must be an array$/i);
  if (match) {
    return formatLocaleMessage(
      'purchaseOrder.receivingModal.error.fieldMustBeArray',
      { field: formatPurchaseOrderReceivingField(match[1]) },
      trimmed,
    );
  }

  return undefined;
};

const resolvePurchaseOrderLocaleKey = (
  code: string,
  payload?: ApiErrorPayload,
): string | undefined => {
  switch (code) {
    case 'OFFER_NOT_FOUND':
      return 'purchaseOrder.error.offerNotFound';
    case 'INVALID_OFFER_STATUS':
      return 'purchaseOrder.error.invalidOfferStatus';
    case 'NO_APPROVED_PRODUCTS':
      return payload?.details?.['hasGeneratedPOProducts']
        ? 'purchaseOrder.error.allApprovedProductsAlreadyGenerated'
        : 'purchaseOrder.error.noApprovedProducts';
    case 'DUPLICATE_PURCHASE_ORDER':
      return 'purchaseOrder.error.duplicatePurchaseOrder';
    case 'INVALID_PAYMENT_TYPE_CONFIG':
      return 'purchaseOrder.error.invalidPaymentTypeConfig';
    case 'GB_SWIFT_BIC_REQUIRED':
      return 'requestError.paymentRequest.gbSwiftBicRequired';
    case 'PRODUCT_MISMATCH':
      return 'purchaseOrder.error.productMismatch';
    case 'INVALID_RECEIVING_STATUS':
      return 'purchaseOrder.error.invalidReceivingStatus';
    case 'EXCEEDING_RECEIVING_QUANTITY':
      return 'purchaseOrder.error.exceedingReceivingQuantity';
    case 'GENERATION_ERROR':
      return 'purchaseOrder.error.generationError';
    default:
      return undefined;
  }
};

const getLocalizedPatternApiErrorMessage = (payload?: ApiErrorPayload): string | undefined => {
  if (!payload) {
    return undefined;
  }

  if (Array.isArray(payload.message)) {
    const localizedMessages = payload.message.map(
      (message) => getLocalizedRawApiMessage(message) || repairMojibakeText(message),
    );
    const combinedMessage = localizedMessages.join('; ');
    if (Array.isArray(payload.fields) && payload.fields.length > 0) {
      const localizedFields = formatFieldList(payload.fields, formatPaymentRequestField);
      return formatLocaleMessage(
        'requestError.paymentRequest.fieldsSuffix',
        { message: combinedMessage, fields: localizedFields },
        `${combinedMessage} (${localizedFields})`,
      );
    }
    return combinedMessage;
  }

  if (typeof payload.message === 'string') {
    const localizedMessage =
      getLocalizedRawApiMessage(payload.message) || repairMojibakeText(payload.message);
    if (Array.isArray(payload.fields) && payload.fields.length > 0) {
      const localizedFields = formatFieldList(payload.fields, formatPaymentRequestField);
      return formatLocaleMessage(
        'requestError.paymentRequest.fieldsSuffix',
        { message: localizedMessage, fields: localizedFields },
        `${localizedMessage} (${localizedFields})`,
      );
    }
    return localizedMessage;
  }

  if (typeof payload.reason === 'string') {
    return getLocalizedRawApiMessage(payload.reason) || repairMojibakeText(payload.reason);
  }

  return undefined;
};

export const getLocalizedApiErrorMessage = (input: any): string | undefined => {
  const payload = normalizePayload(input);
  const code = payload?.code;

  if (code === 'PURCHASE_VARIANCE_ORDERS_ALREADY_PAID' && Array.isArray(payload?.details?.orders)) {
    const orders = (payload.details.orders as any[]).map(order => {
      const amount = Number(order.paid_amount);
      const total = Number(order.total_amount);
      const status = amount > 0 && total > 0
        ? `${order.currency || 'HKD'} ${amount.toFixed(2)}/${total.toFixed(2)} (${Number((amount / total * 100).toFixed(2))}%)`
        : formatLocaleMessage('requestError.paymentAllocation.paidAmountUnknown');
      return `${order.order_number || order.id} (${status})`;
    }).join(', ');
    return formatLocaleMessage('requestError.paymentAllocation.priorPayment', { orders });
  }

  const allocationErrors: Record<string, string> = {
    PURCHASE_PAYMENT_EXCEEDS_REMAINING: 'remaining',
    PURCHASE_PAYMENT_EXCEEDS_DOCUMENT: 'document',
    PURCHASE_PAYMENT_INVALID_REMAINING: 'invalidRemaining',
  };
  if (code && allocationErrors[code] && payload?.details) {
    const details = payload.details;
    const money = (field: string) => typeof details[field] === 'number' && Number.isFinite(details[field])
      ? (details[field] as number).toFixed(2) : '—';
    return formatLocaleMessage(`requestError.paymentAllocation.${allocationErrors[code]}`, {
      order: String(details.order_number || details.purchase_order_id || '—'),
      currency: String(details.currency || 'HKD'),
      payment: money('payment_amount'), remaining: money('remaining_amount'), document: money('document_amount'),
    });
  }

  if (code) {
    const localeKey = resolvePurchaseOrderLocaleKey(code, payload);
    if (localeKey) {
      return formatLocaleMessage(localeKey, undefined, getRawMessage(payload));
    }
  }

  return getLocalizedPatternApiErrorMessage(payload);
};

export const getApiErrorDisplayMessage = (input: any, fallback?: string): string => {
  const payload = normalizePayload(input);

  return (
    getLocalizedApiErrorMessage(payload) ||
    getRawMessage(payload) ||
    repairMojibakeText(fallback) ||
    formatLocaleMessage('requestError.unknown')
  );
};
